import { useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router';
import { useList } from '@refinedev/core';
import {
  BarChart3,
  Building2,
  ChevronDown,
  ClipboardCheck,
  ClipboardList,
  Code2,
  FileSignature,
  FileText,
  HardHat,
  History,
  LayoutDashboard,
  UploadCloud,
  UserRound,
  Users,
  type LucideIcon,
} from 'lucide-react';
import { describeRole, type Capability } from 'shared';
import { BrandMark } from '../../components/BrandMark.js';
import { SessionBar } from '../../components/SessionBar.js';
import { SettingsPanel } from '../../components/SettingsPanel.js';
import { StaffQuickActions } from '../../components/StaffQuickActions.js';
import { MessagingPill } from '../../components/MessagingPill.js';
import { MessagingPanel } from '../../components/MessagingPanel.js';
import { MessagingConsentGate } from '../../components/MessagingConsentGate.js';
import { MessagingProvider } from '../../lib/messaging-context.js';
import { useActor, useCapabilities } from '../../lib/capabilities.js';

/**
 * Staff shell.
 *
 * The nav is a plain list of links rather than driven off Refine's `resources`
 * config. That config exists to power Refine's own menu components, and with no UI
 * kit installed there is no menu component to power — so it would be a second place
 * to edit whenever a link changes, with nothing reading it.
 *
 * ---------------------------------------------------------------------------
 * Why links are hidden rather than disabled
 * ---------------------------------------------------------------------------
 *
 * Four staff roles share this console. A maintenance tech has no use for Owners and
 * would get a 403 from `owners:read`, so the link is absent — a greyed-out link is
 * an invitation to ask why, and a live link onto a 403 reads as a broken app.
 *
 * Hiding is not protecting. Typing /staff/owners still renders the screen, and the
 * screen still asks the API, and the API still refuses. That is the order things
 * are meant to fail in.
 */
interface NavLinkDef {
  to: string;
  label: string;
  icon: LucideIcon;
  capability?: Capability | undefined;
  end?: boolean | undefined;
}

interface NavItemDef {
  to: string;
  label: string;
  capability?: Capability | undefined;
}

interface NavGroupDef {
  label: string;
  icon: LucideIcon;
  items: NavItemDef[];
  /** Attention dot on the trigger's corner — see the `Maintenance` entry's own comment. */
  badge?: boolean | undefined;
}

/**
 * Rent Roll and Delinquency are the one pair that share both a capability
 * (`ledger:read`) and a subject (the same lease balances, sliced two ways) —
 * that's what makes collapsing them into one dropdown a natural fit rather
 * than an arbitrary space-saving trick applied to any two links. Nothing
 * else in this bar shares that relationship, so this stays a single group
 * rather than a general "categorize every link" pass.
 */
const LINKS: (NavLinkDef | NavGroupDef)[] = [
  { to: '/staff', label: 'Dashboard', icon: LayoutDashboard, end: true },
  { to: '/staff/properties', label: 'Properties', icon: Building2 },
  { to: '/staff/owners', label: 'Owners', icon: UserRound, capability: 'owners:read' },
  { to: '/staff/tenants', label: 'Tenants', icon: Users, capability: 'tenants:read' },
  { to: '/staff/leases', label: 'Leases', icon: FileSignature, capability: 'leases:read' },
  {
    to: '/staff/document-templates',
    label: 'Document Templates',
    icon: FileText,
    capability: 'leases:read',
  },
  {
    label: 'Applications',
    icon: ClipboardCheck,
    items: [
      { to: '/staff/applications', label: 'Queue', capability: 'applications:read' },
      {
        to: '/staff/screening-criteria',
        label: 'Screening criteria',
        capability: 'applications:read',
      },
    ],
  },

  { to: '/staff/reports', label: 'Reports', icon: BarChart3, capability: 'reports:read' },
  {
    label: 'Maintenance',
    icon: HardHat,
    items: [
      { to: '/staff/work-orders', label: 'Work orders', capability: 'maintenance:read' },
      { to: '/staff/vendors', label: 'Vendors', capability: 'maintenance:read' },
      // reports:read, not maintenance:read like its siblings above — this
      // report surfaces the cost figure across the whole portfolio, the same
      // "financial reports are not for MAINTENANCE" line CLAUDE.md draws for
      // the Reports module (see Payments History's identical comment under
      // Payment Center, below).
      { to: '/staff/maintenance-reports', label: 'Reports', capability: 'reports:read' },
    ],
  },
  { to: '/staff/audit-log', label: 'Audit log', icon: History, capability: 'audit:read' },
  { to: '/staff/import', label: 'Onboarding', icon: UploadCloud, capability: 'import:write' },
  {
    label: 'Payment Center',
    icon: ClipboardList,
    items: [
      { to: '/staff/rent-roll', label: 'Rent Roll', capability: 'ledger:read' },
      { to: '/staff/delinquency', label: 'Delinquency', capability: 'ledger:read' },
      { to: '/staff/owner-disbursements', label: 'Owner Disbursements', capability: 'ledger:read' },
      { to: '/staff/owner-statements', label: 'Owner Statements', capability: 'ledger:read' },
      {
        to: '/staff/vendor-disbursements',
        label: 'Vendor Disbursements',
        capability: 'ledger:read',
      },
      // reports:read, not ledger:read like its siblings above — this one
      // reaches across every tenant and owner by an arbitrary caller-chosen
      // id, the same "financial reports are not for MAINTENANCE" line
      // CLAUDE.md already draws for the Reports module, not ledger:read's
      // "everyone" grant (see PaymentsHistory.tsx's own header comment).
      { to: '/staff/payments-history', label: 'Payments History', capability: 'reports:read' },
    ],
  },
  {
    label: 'Developer',
    icon: Code2,
    items: [
      {
        to: '/staff/developer/test-emails',
        label: 'Test Emails',
        // DEVELOPER only, not ADMIN — see `developer:access`'s own doc
        // comment in roles.ts. The endpoints this page calls
        // (POST .../document-templates/preview, .../send-test-email) are
        // still gated on document-templates:write underneath, unchanged,
        // because ADMIN legitimately needs those same two endpoints for the
        // real Document Templates composer's own preview/test-send buttons.
        // This capability only keeps the menu entry itself out of ADMIN's
        // nav — same "hiding is not protecting" posture as everywhere else
        // in this file, so an ADMIN who navigates here directly can still
        // use the page, exactly as they could before this change.
        capability: 'developer:access',
      },
    ],
  },
];

/**
 * Same dropdown mechanics as SettingsPanel's gear icon and TenantForm.tsx's
 * lease-count pill: relative wrapper, absolute panel, outside-click/Escape
 * closes it. The trigger reads active when the current route matches any
 * child, so landing on /staff/delinquency directly (a bookmark, a link from
 * the dashboard) still highlights the group rather than looking unvisited.
 */
function NavGroup({ label, icon: Icon, items, badge }: NavGroupDef) {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const active = items.some((item) => location.pathname.startsWith(item.to));

  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: PointerEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false);
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false);
    }
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <div className="pill-menu" ref={ref}>
      <button
        type="button"
        className={
          active
            ? 'pill-menu__trigger shell__nav-link shell__nav-link--active'
            : 'pill-menu__trigger shell__nav-link'
        }
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <Icon size={16} strokeWidth={1.8} aria-hidden="true" />
        {label}
        <ChevronDown size={14} strokeWidth={1.8} aria-hidden="true" />
      </button>
      {badge ? <span className="pill-menu__dot" aria-label="New" /> : null}

      {open ? (
        <div className="pill-menu__panel card">
          {items.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                isActive ? 'pill-menu__item pill-menu__item--active' : 'pill-menu__item'
              }
              onClick={() => setOpen(false)}
            >
              {item.label}
            </NavLink>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export function StaffLayout() {
  const capabilities = useCapabilities();
  const actor = useActor();
  const canReadMaintenance = capabilities.includes('maintenance:read');

  // Red dot on the Maintenance pill when a work order is sitting at OPEN —
  // the status every request starts at, staff or tenant-submitted alike, so
  // it's the cheapest available proxy for "not yet triaged" without a
  // separate seen/unseen table. Query only fires for roles that can actually
  // see work orders; React Query's own default refetch-on-refocus is what
  // keeps it from going stale across a long-open tab, same as everywhere
  // else in this app that reads through Refine's `useList` with no custom
  // polling of its own.
  const { data: openWorkOrders } = useList({
    resource: 'work-orders',
    filters: [{ field: 'status', operator: 'eq', value: 'OPEN' }],
    pagination: { mode: 'off' },
    queryOptions: { enabled: canReadMaintenance },
  });
  const hasOpenWorkOrders = canReadMaintenance && (openWorkOrders?.total ?? 0) > 0;

  const visible = LINKS.flatMap((link): (NavLinkDef | NavGroupDef)[] => {
    if ('items' in link) {
      const items = link.items.filter(
        (item) => !item.capability || capabilities.includes(item.capability),
      );
      if (items.length === 0) return [];
      const badge = link.label === 'Maintenance' ? hasOpenWorkOrders : undefined;
      return [{ ...link, items, badge }];
    }
    return !link.capability || capabilities.includes(link.capability) ? [link] : [];
  });

  return (
    <MessagingProvider>
      <div className="shell">
        <header className="shell__header">
          {/* Its own row, separate from `.shell__nav` below — the pill needs a
              `position: relative` anchor scoped to JUST this row's height, not
              the whole (multi-line, once nav wraps) header, or centering it
              vertically at 50% of the full header would land it over the nav
              row instead of this one. */}
          <div className="shell__header-row">
            <BrandMark />
            {/* Absolutely centered in this row — see styles.css's
                `.messaging-pill`/`.messaging-panel`. Deliberately not part of
                the flex flow the rest of the row uses, so it stays centered
                regardless of how wide the brand/badge cluster on the left or
                the session/settings cluster on the right happen to be. */}
            <MessagingPill />
            <MessagingPanel />
            <MessagingConsentGate />
            {/* The role, not just "Staff". Four roles see four different consoles, and
                "why can't I see Owners" is answered before it is asked.
                ADMIN gets "Commander" here specifically — matching the login door's
                "{brand} Commander" heading — rather than through describeRole(),
                which also drives the dashboard welcome text, the "already signed
                in" screen, and API error copy ("Administrator accounts cannot do
                this."); none of those asked for the same relabel. */}
            <span className="shell__badge">
              {actor?.staffRole === 'ADMIN' ? 'Commander' : describeRole('STAFF', actor?.staffRole)}
            </span>
            {/* showSignOutButton=false — StaffQuickActions' door icon,
                right after SettingsPanel, is this header's sign-out
                affordance. SessionBar still renders the "signed in as…"
                text on its own. */}
            <SessionBar showSignOutButton={false} />
            <SettingsPanel />
            <StaffQuickActions />
          </div>
          {/* Its own full-width row rather than squeezed between the badge and the
              email/Sign out cluster on the row above — this is the last header
              child so nothing else gets pulled down onto a third row with it.
              Four staff roles put very different numbers of links here; giving
              the pill bar the whole header width is what keeps ADMIN's
              dozen-odd links from wrapping into a cramped two lines squeezed
              into leftover space. */}
          <nav className="shell__nav">
            {visible.map((link) =>
              'items' in link ? (
                <NavGroup
                  key={link.label}
                  label={link.label}
                  icon={link.icon}
                  items={link.items}
                  badge={link.badge}
                />
              ) : (
                <NavLink
                  key={link.to}
                  to={link.to}
                  end={link.end ?? false}
                  className={({ isActive }) =>
                    isActive ? 'shell__nav-link shell__nav-link--active' : 'shell__nav-link'
                  }
                >
                  <link.icon size={16} strokeWidth={1.8} aria-hidden="true" />
                  {link.label}
                </NavLink>
              ),
            )}
          </nav>
        </header>
        <main className="shell__main shell__main--wide">
          <Outlet />
        </main>
      </div>
    </MessagingProvider>
  );
}
